import '@/Analytics';
import Vue from 'vue';
import Config from '@/Config';
import PlatformCapabilities from '@/Capabilities';
import Resources from '@/Resources.js';
import '@/DisplayMode';
import '@/FrameworkConfig.js';

import { BaseNotificator } from '@/Components/Base';
import BlogPostBreadcrumb from '@/Components/Blog/BlogPostBreadcrumb.vue';
import BlogPostRestrictedVisibility from '@/Components/Blog/BlogPostRestrictedVisibility.vue';
import Field from '@/Components/Field.vue';
import Localization from '@/Components/Localization';
import RequestResetPasswordForm from '@/Views/Users/RequestResetPasswordForm.vue';
import CartsComponents from '@/Components/Carts';
import OrdersComponents from '@/Components/Orders';
import PaymentComponents from '@/Components/Payments';
import UserComponents from '@/Components/Users';
import SearchComponents from '@/Components/Search';
import SystemStatusComponents from '@/Components/SystemStatus';
import Tooltip from '@/Components/Tooltip.vue';
import NewsletterComponents from '@/Components/Newsletter';
import ProjectsComponents from '@/Components/Projects';
import TableOfContents from '@/Components/TableOfContents.vue';
import CheckoutAuthenticateForm from '@/Components/Users/CheckoutAuthenticateForm.vue';
import MessageBox from '@/Components/MessageBox.vue';
import TestimonialsSlider from '@/Components/TestimonialsSlider.vue';
import CreatorBannerSlider from '@/Components/CreatorBannerSlider.vue';
import ProductInfoView from '@/Components/ProductDetails/ProductInfoView.vue';
import SocialMediaActions from '@/Components/SocialMediaActions.vue';
import CurrencySelector from '@/Components/CurrencySelector';
import BecomeACreatorSection from '@/Components/Creator/BecomeACreatorSection';
import BecomeACreatorStats from '@/Components/Creator/BecomeACreatorStats';
import BecomeACreatorValues from '@/Components/Creator/BecomeACreatorValues';
import CreatorProjects from '@/Components/Creator/CreatorProjects';
import ProjectAdvertBanner from '@/Components/ProjectAdvertBanners/ProjectAdvertBanner';
import Contact from '@/Components/Contact/ContactView.vue';
import TeamMembersSlider from '@/Components/TeamMembersSlider.vue';
import PartnersSlider from '@/Components/PartnersSlider.vue';
import EmptyState from '@/Components/EmptyState.vue';
import FloatingMessagesWrapper from '@/Components/FloatingMessagesWrapper.vue';
import ServerNotificationHandler from '@/Components/ServerNotificationHandler';
import ClientAnalyticsEventHandler from './Components/ClientAnalyticsEventHandler.vue';
import ProjectBanner from '@/Components/Gallery/ProjectBanner.vue';
import LegacyProjectBanner from '@/Components/Gallery/LegacyProjectBanner.vue';
import ProgressOverlay from '@/Components/ProgressOverlay.vue';
import LoadingIndicator from '@/Components/LoadingIndicator.vue';
import Poll from '@/Components/Poll';
import FooterNavigation from '@/Components/FooterNavigation.vue';
import HeaderActions from '@/Components/TheHeader/HeaderActions.vue';
import CommentsSection from '@/Components/Comments/CommentsSection.vue';
import ProjectHomeDetails from '@/Components/Projects/ProjectHomeDetails.vue';
import UserLocation from '@/Components/UserLocation';
import Footer from '@/Components/Layout/Footer.vue';
import LoginButton from '@/Components/Login/LoginButton.vue';
import SignUpButton from './Components/CreateAccount/SignUpButton.vue';
import HeaderLogo from '@/Components/TheHeader/HeaderLogo.vue';
import TopNavigationBar from '@/Components/TopNavigationBar.vue';
import PushNotificationModal from '@/Components/PushNotification/PushNotificationModal.vue';
import BecomeCreatorContent from '@/Components/Creator/BecomeCreatorContent.vue';
import SkipNavigation from './Components/SkipNavigation.vue';

import Plugins from './Plugins';
import Stores from './Stores';

import Bus from './Bus';
import Router from './Router';
import Formatter from './Utils/Formatter';
import Scroller from './Utils/Scroller';
import VideoUtility from './Utils/VideoUtility';
import Formatting from './Utils/Formatting';

import { checkElementViewportFit, debounce } from './Utils';
import { EventBus } from './Utils/EventBus';

import RealTimeServiceInstance from './Services/RealTimeServiceInstance';

// Views (in future this will be lazy loaded)
import AboutUsView from '@/Views/AboutUsView.vue';
import AccountSettingsView from '@/Views/Users/AccountSettingsView.vue';
import AccountSubmittedView from '@/Views/AccountSubmittedView.vue';
import BeginOrderView from '@/Views/BeginOrderView.vue';
import CompleteUserAccountView from '@/Views/Users/CompleteUserAccountView.vue';
import ContactRequestSentView from '@/Views/Contact/ContactRequestSentView.vue';
import CreateAccountView from '@/Views/CreateAccountView.vue';
import CreateOrderView from '@/Views/Orders/CreateOrderView.vue';
import CreatorAccountView from '@/Views/Creator/CreatorAccountView.vue';
import CreatorView from '@/Views/Creator/CreatorView.vue';
import FaqView from '@/Views/Faq/FaqView.vue';
import HomepageView from '@/Views/Home/HomepageView.vue';
import JoinProjectConfirmationView from '@/Views/JoinProjectConfirmationView.vue';
import KYCCompletedView from '@/Views/KYCCompletedView.vue';
import LegacyBeginOrderView from '@/Views/LegacyBeginOrderView.vue';
import LegacyCreateOrderView from '@/Views/Orders/LegacyCreateOrderView.vue';
import LoginView from '@/Views/Users/LoginView.vue';
import BackerCenterDashboardView from '@/Views/BackerCenter/BackerCenterDashboardView.vue';
import BackerCenterPaymentsView from '@/Views/BackerCenter/BackerCenterPaymentsView.vue';
import BackerCenterPledgesAndOrdersView from '@/Views/BackerCenter/BackerCenterPledgesAndOrdersView.vue';
import BackerCenterProjectsView from '@/Views/BackerCenter/BackerCenterProjectsView.vue';
import BackerCenterUpdatesFeedView from '@/Views/BackerCenter/BackerCenterUpdatesFeedView.vue';
import PasswordChangedView from '@/Views/Users/PasswordChangedView.vue';
import PaymentStatusView from '@/Views/Payments/PaymentStatusView.vue';
import PaymentSuccessView from '@/Views/Payments/PaymentSuccessView.vue';
import PledgeOrderView from '@/Views/Orders/PledgeOrderView.vue';
import ProjectContactRequestSentView from '@/Views/Contact/ProjectContactRequestSentView.vue';
import ResetPasswordRequestSentView from '@/Views/Users/ResetPasswordRequestSentView.vue';
import ResetPasswordView from '@/Views/Users/ResetPasswordView.vue';
import ResumePaymentView from '@/Views/Payments/ResumePaymentView.vue';
import RewardsView from '@/Views/Project/RewardsView.vue';
import SelectOrderView from '@/Views/SelectOrderView.vue';
import SubscriptionManagementView from '@/Views/Users/SubscriptionManagementView.vue';
import TermsOfServicesView from '@/Views/TermsOfServicesView.vue';
import TermsOfServiceView from '@/Views/TermsOfServiceView.vue';
import UnsubscribeNotificationView from '@/Views/UnsubscribeNotificationView';
import UserProfileView from '@/Views/UserProfile/UserProfileView.vue';
import UserProfileBanner from '@/Views/UserProfile/UserProfileBanner.vue';
import PerformanceMarketingView from '@/Views/StaticPages/PerformanceMarketingView.vue';
import WhyGamefoundView from '@/Views/StaticPages/WhyGamefoundView.vue';
import CrowdfundingGuideView from '@/Views/StaticPages/CrowdfundingGuideView.vue';
import BlogListView from '@/Views/Blog/BlogListView.vue';
import BlogPostDetailsCard from '@/Components/Blog/BlogPostDetailsCard.vue';

// Platform specific views
import FeesView from '@platform/Views/StaticPages/FeesView.vue';
import MarketingServicesView from '@platform/Views/StaticPages/MarketingServicesView.vue';

const Components = {
    registeredComponents: {},
    // component imports
    BaseNotificator,
    BecomeACreatorSection,
    BecomeACreatorStats,
    BecomeACreatorValues,
    BlogPostBreadcrumb,
    BlogPostRestrictedVisibility,
    Carts: CartsComponents,
    CheckoutAuthenticateForm,
    Contact,
    CommentsSection,
    CreatorBannerSlider,
    CreatorProjects,
    CurrencySelector,
    EmptyState,
    Field,
    FooterNavigation,
    Localization,
    MessageBox,
    Newsletter: NewsletterComponents,
    Orders: OrdersComponents,
    PartnersSlider,
    Payments: PaymentComponents,
    Poll,
    ProgressOverlay,
    LoadingIndicator,
    ProjectAdvertBanner,
    ProjectBanner,
    LegacyProjectBanner,
    Projects: ProjectsComponents,
    RequestResetPasswordForm,
    Search: SearchComponents,
    ServerNotificationHandler,
    ClientAnalyticsEventHandler,
    SocialMediaActions,
    SystemStatus: SystemStatusComponents,
    TableOfContents,
    TeamMembersSlider,
    TestimonialsSlider,
    Tooltip,
    Users: UserComponents,
    UserProfileBanner,
    HeaderActions,
    HeaderLogo,
    ProjectHomeDetails,
    FloatingMessagesWrapper,
    UserLocation,
    Footer,
    LoginButton,
    SignUpButton,
    TopNavigationBar,
    PushNotificationModal,
    BecomeCreatorContent,
    SkipNavigation,
};

const Utils = {
    checkElementViewportFit,
    debounce,
    EventBus,
    Formatter,
    VideoUtility,
    Formatting,
    Scroller,
};

const Views = {
    AboutUsView,
    AccountSettingsView,
    AccountSubmittedView,
    BackerCenterDashboardView,
    BackerCenterPaymentsView,
    BackerCenterPledgesAndOrdersView,
    BackerCenterProjectsView,
    BackerCenterUpdatesFeedView,
    BeginOrderView,
    CompleteUserAccountView,
    ContactRequestSentView,
    CreateAccountView,
    CreateOrderView,
    CreatorAccountView,
    CreatorView,
    CrowdfundingGuideView,
    FaqView,
    FeesView,
    HomepageView,
    JoinProjectConfirmationView,
    KYCCompletedView,
    LegacyBeginOrderView,
    LegacyCreateOrderView,
    LoginView,
    MarketingServicesView,
    PasswordChangedView,
    PaymentStatusView,
    PaymentSuccessView,
    PledgeOrderView,
    ProductInfoView,
    ProjectContactRequestSentView,
    ResetPasswordRequestSentView,
    ResetPasswordView,
    ResumePaymentView,
    RewardsView,
    SelectOrderView,
    SubscriptionManagementView,
    TermsOfServicesView,
    TermsOfServiceView,
    UnsubscribeNotificationView,
    UserProfileView,
    PerformanceMarketingView,
    WhyGamefoundView,
    BlogListView,
    BlogPostDetailsCard,
};

const App = {
    Bus,
    Components,
    Config,
    PlatformCapabilities,
    Plugins,
    RealTimeServiceInstance,
    Resources,
    Router,
    Stores,
    Utils,
    Views,
};

App.registerComponent = (componentId, name, path, options) => {
    Components.registeredComponents[componentId] = new Vue({
        el: `#${componentId}`,
        name,
        router: Router,
        pinia: Stores,
        render: (h) => h(path, options),
    });
};

window.App = App;